import React, { useEffect } from "react";
import SubstepValue from "./SubstepValue";
import {
  isDisclosureRadioItems,
  SUBSTEP_TYPE,
} from "utils/constants/onboarding";
import {
  isPrimitiveRequired,
  isPrimitiveSatisfied,
} from "../fieldRules";
import { friendlyBankError, useBankAutofill } from "./useBankAutofill";
import {
  buildPrimitiveValueByKey,
  findPrimitiveByKey,
  getSelectedDisclosureItem,
  hasTodoPrimitives,
} from "./todoListUtils";
import styles from "../Form.module.scss";

function TodoList({ substep, onChange, error, isReadOnly, allValues, allErrors, valueByKey }) {
  const items = substep?.input_todo_list?.items ?? [];

  const accNumPrim = findPrimitiveByKey(items, "account_number_input");
  const confirmPrim = findPrimitiveByKey(items, "confirm_account_number_input");
  const ifscPrim = findPrimitiveByKey(items, "ifsc_code_input");
  const holderPrim = findPrimitiveByKey(items, "account_holder_name_input");
  const bankNamePrim = findPrimitiveByKey(items, "bank_name_input");
  const bankAddressPrim = findPrimitiveByKey(items, "bank_address_input");
  const accountTypePrim = findPrimitiveByKey(items, "account_type_input");
  const hasPrimitives = hasTodoPrimitives(items);
  const isDisclosureRadio = isDisclosureRadioItems(items);
  const valueOf = (id) => allValues?.[id];
  const errorOf = (id) => allErrors?.[id];
  const primitiveValueByKey = buildPrimitiveValueByKey(items, allValues);
  const combinedValueByKey = { ...valueByKey, ...primitiveValueByKey };
  const isRequired = (prim) => isPrimitiveRequired(prim, combinedValueByKey);
  const isSatisfied = (prim) => isPrimitiveSatisfied(prim, allValues, combinedValueByKey);

  const bank = useBankAutofill({
    primitives: {
      accNumPrim,
      confirmPrim,
      ifscPrim,
      holderPrim,
      bankNamePrim,
      bankAddressPrim,
      accountTypePrim,
    },
    allValues,
    onChange,
  });

  // Auto-set parent item bool_value based on whether all its primitives are filled.
  // Skipped for disclosure radio — their bool_value comes from the radio click only.
  useEffect(() => {
    if (!hasPrimitives || isDisclosureRadio) return;
    items.forEach((item) => {
      const primitives = item.primitive_inputs ?? [];
      if (!primitives.length) return;
      const allFilled = primitives.every(isSatisfied);
      const currentBool = valueOf(item.substep_id)?.bool_value ?? false;
      if (allFilled !== currentBool) onChange(item.substep_id, { bool_value: allFilled });
    });
    
  }, [allValues]);

  const handleRadioToggle = (clickedId) => {
    if (isReadOnly) return;
    items.forEach((it) => {
      if (it.substep_id === clickedId) {
        onChange(it.substep_id, { bool_value: true });
      } else {
        onChange(it.substep_id, null);
        if (isDisclosureRadio) {
          for (const prim of it.primitive_inputs ?? []) {
            // IMAGE_INPUT: send [] so the server clears the stored S3 path in DraftValueMap.
            // null is filtered out by saveEditedSubsteps and never reaches the server.
            onChange(prim.substep_id, prim.substep_type === SUBSTEP_TYPE.IMAGE_INPUT
              ? { string_list_value: [] }
              : null);
          }
        }
      }
    });
  };

  if (!hasPrimitives) {
    return (
      <div className={styles.todoList}>
        {items.map((item) => {
          const checked = !!valueOf(item.substep_id)?.bool_value;
          const itemError = errorOf(item.substep_id);
          return (
            <div key={item.substep_id} className={styles.todoRadioItem}>
              <label className={`${styles.todoRadioLabel} ${checked ? styles.todoRadioLabelSelected : ""}`}>
                <input
                  type="radio"
                  name={`todo_${substep.substep_id}`}
                  checked={checked}
                  disabled={isReadOnly}
                  onChange={() => handleRadioToggle(item.substep_id)}
                />
                <span className={styles.todoTitle}>{item.title}</span>
                {item.subtitle && <span className={styles.todoSubtitle}>{item.subtitle}</span>}
              </label>
              {itemError && <div className={styles.fieldError}>{itemError}</div>}
            </div>
          );
        })}
        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
    );
  }

  if (isDisclosureRadio) {
    // Both options may be stored as true (proto3 patch for MARK_STEP_COMPLETE).
    // Resolve the real selection by preferring the item whose primitives are filled.
    const selectedItem = getSelectedDisclosureItem(items, allValues);

    return (
      <div className={styles.todoList}>
        {items.map((item) => {
          const checked = item.substep_id === selectedItem?.substep_id;
          const itemError = errorOf(item.substep_id);
          return (
            <div key={item.substep_id} className={styles.todoRadioItem}>
              <label className={`${styles.todoRadioLabel} ${checked ? styles.todoRadioLabelSelected : ""}`}>
                <input
                  type="radio"
                  name={`todo_${substep.substep_id}`}
                  checked={checked}
                  disabled={isReadOnly}
                  onChange={() => handleRadioToggle(item.substep_id)}
                />
                <span className={styles.todoTitle}>{item.title}</span>
                {item.subtitle && <span className={styles.todoSubtitle}>{item.subtitle}</span>}
              </label>
              {itemError && <div className={styles.fieldError}>{itemError}</div>}
            </div>
          );
        })}

        {selectedItem && (selectedItem.primitive_inputs ?? []).length > 0 && (
          <div className={styles.todoPrimitives}>
            {selectedItem.primitive_inputs.map((prim) => {
              const primTitle = prim.title !== selectedItem.title ? prim.title : null;
              const primSubtitle = prim.subtitle !== selectedItem.subtitle ? prim.subtitle : null;
              return (
                <div key={prim.substep_id} className={styles.field}>
                  {primTitle && (
                    <label className={styles.label}>
                      {primTitle}
                      {isRequired(prim) && (
                        <span className={styles.requiredMark}>*</span>
                      )}
                    </label>
                  )}
                  {primSubtitle && <div className={styles.muted}>{primSubtitle}</div>}
                  <SubstepValue
                    substep={prim}
                    value={valueOf(prim.substep_id)}
                    onChange={onChange}
                    error={errorOf(prim.substep_id)}
                    allValues={allValues}
                    allErrors={allErrors}
                    valueByKey={combinedValueByKey}
                    isReadOnly={isReadOnly}
                  />
                </div>
              );
            })}
          </div>
        )}

        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
    );
  }

  // Primitive labels that duplicate any item's section title are redundant — suppress them.
  const itemTitleSet = new Set(items.map((it) => it.title).filter(Boolean));
  const bankAutofillPrimIds = [
    holderPrim?.substep_id,
    bankNamePrim?.substep_id,
    bankAddressPrim?.substep_id,
  ].filter(Boolean);
  const bankAutofillItemIds = items
    .filter((item) =>
      (item.primitive_inputs ?? []).some((prim) =>
        bankAutofillPrimIds.includes(prim.substep_id)
      )
    )
    .map((item) => item.substep_id);
  const firstBankAutofillItemId = bankAutofillItemIds[0];
  const lastBankAutofillItemId = bankAutofillItemIds[bankAutofillItemIds.length - 1];
  const lastBankAutofillPrimId = bankAutofillPrimIds[bankAutofillPrimIds.length - 1];

  return (
    <div className={styles.todoList}>
      {items.map((item) => {
        const primitives = item.primitive_inputs ?? [];
        const isBankAutofillItem = bankAutofillItemIds.includes(item.substep_id);

        // When ALL primitive labels are redundant the * moves up to the section header.
        const allLabelsRedundant = primitives.length > 0 && primitives.every((p) => itemTitleSet.has(p.title));
        const anyPrimRequired = primitives.some(isRequired);
        const sectionClass = [
          styles.todoSection,
          isBankAutofillItem ? styles.bankAutofillGroupSection : "",
          item.substep_id === firstBankAutofillItemId ? styles.bankAutofillGroupFirst : "",
          item.substep_id === lastBankAutofillItemId ? styles.bankAutofillGroupLast : "",
        ].filter(Boolean).join(" ");

        return (
          <div key={item.substep_id} className={sectionClass}>
            {(item.title || item.subtitle) && (
              <div className={styles.todoSectionHeader}>
                {item.title && (
                  <span className={styles.todoTitle}>
                    {item.title}
                    {allLabelsRedundant && anyPrimRequired && (
                      <span className={styles.requiredMark}>*</span>
                    )}
                  </span>
                )}
                {item.subtitle && <span className={styles.todoSubtitle}>{item.subtitle}</span>}
              </div>
            )}

            {primitives.length > 0 && (
              <div className={styles.todoPrimitives}>
                {primitives.map((prim) => {
                  const isAutoFilledBankField =
                    bank.canBankVerify &&
                    (prim.substep_id === holderPrim.substep_id ||
                      prim.substep_id === bankNamePrim?.substep_id ||
                      prim.substep_id === bankAddressPrim?.substep_id);
                  const showBankStatusHere =
                    bank.canBankVerify &&
                    prim.substep_id === lastBankAutofillPrimId &&
                    (bank.bankVerifying || bank.bankError || bank.bankVerified);
                  const isConfirmField = bank.canBankVerify && prim.substep_id === confirmPrim.substep_id;
                  const primTitle = itemTitleSet.has(prim.title) ? null : prim.title;
                  const primSubtitle = prim.subtitle !== item.subtitle ? prim.subtitle : null;
                  const isPrimRequired = isRequired(prim);
                  const fieldError = isConfirmField && bank.confirmMismatch
                    ? "Account numbers don't match"
                    : errorOf(prim.substep_id);

                  return (
                    <div key={prim.substep_id} className={`${styles.field}${isAutoFilledBankField ? ` ${styles.holderNameField}` : ""}`}>
                      {primTitle && (
                        <label className={styles.label}>
                          {primTitle}
                          {isPrimRequired && (
                            <span className={styles.requiredMark}>*</span>
                          )}
                        </label>
                      )}
                      {primSubtitle && <div className={styles.muted}>{primSubtitle}</div>}
                      <SubstepValue
                        substep={prim}
                        value={valueOf(prim.substep_id)}
                        onChange={onChange}
                        error={fieldError}
                        allValues={allValues}
                        allErrors={allErrors}
                        isReadOnly={isReadOnly || isAutoFilledBankField}
                        holderNameBold={isAutoFilledBankField}
                      />
                      {isConfirmField && bank.confirmMatch && (
                        <div className={styles.fieldSuccess}>Account numbers match</div>
                      )}
                      {showBankStatusHere && (
                        <div
                          className={`${styles.bankStatusCard} ${
                            bank.bankError && !bank.bankVerified
                              ? styles.bankStatusCardError
                              : bank.bankVerified && !bank.bankError
                                ? styles.bankStatusCardSuccess
                                : styles.bankStatusCardPending
                          }`}
                        >
                          <span className={styles.bankStatusIcon}>
                            {bank.bankVerifying ? <span className={styles.spinner} /> : bank.bankError && !bank.bankVerified ? "!" : "✓"}
                          </span>
                          <span>
                            {bank.bankVerifying
                              ? "Verifying bank account and fetching bank details..."
                              : bank.bankError && !bank.bankVerified
                                ? friendlyBankError(bank.bankError)
                                : "Bank details verified"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
}

export default TodoList;
